import { Alert } from "@mui/material";
import { ProgressButton, useToast } from "@qubit/autoparts";
import { t } from "i18next";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "~/app/store";

import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useNextEmptyBinMutation } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";

import { useConfirmBinReconfigurationMutation } from "~/redux/warehouse/workstation.hooks";

export const ConfirmBinReconfigurationButton = ({
  portId,
  portSideIndex
}: {
  portId: number;
  portSideIndex: number;
}) => {
  const { successToast } = useToast();
  const [queryParams] = useSearchParams();
  const sourceBinConfigurationId = queryParams.get("sourceBinConfigurationId");
  const targetBinConfigurationId = queryParams.get("targetBinConfigurationId");
  const trackedPageName = useMixpanelPageName();

  const [
    nextEmptyBin,
    { error: nextEmptyBinError, isLoading: nextEmptyBinLoading }
  ] = useNextEmptyBinMutation();
  const [
    confirmBinReconfiguration,
    {
      reset: confirmBinReconfigurationReset,
      isLoading: confirmBinReconfigurationLoading,
      error: confirmBinReconfigurationError
    }
  ] = useConfirmBinReconfigurationMutation();
  const workstation = useAppSelector(selectThisWorkstation);

  const { data: binConfigurations } = useGetBinConfigurationsQuery();

  const targetBinConfiguration = binConfigurations?.find(
    (bc) => bc.configurationId === targetBinConfigurationId
  );

  const { selectedBin, activityState } = usePortStatusQuery(
    portId,
    portSideIndex
  );

  const handleConfirm = async (isRetry = false) => {
    if (!workstation || !sourceBinConfigurationId || !targetBinConfigurationId)
      return;

    confirmBinReconfigurationReset();
    try {
      if (activityState === "Bin Opened" && selectedBin) {
        mixpanelTrack({
          trackedPageName,
          label: isRetry
            ? "Retry Confirm Configuration"
            : "Confirm Configuration",
          type: "Button Click",
          eventProperties: {
            "Port Side Index": portSideIndex,
            "Source Bin": selectedBin,
            "Source Bin Configuration Id": sourceBinConfigurationId,
            "Target Bin Configuration Id": targetBinConfigurationId
          }
        });
        await confirmBinReconfiguration({
          workstationId: workstation.id,
          sourceBinConfigurationId,
          targetBinConfigurationId,
          binNumber: selectedBin
        }).unwrap();

        successToast(
          `Reconfigured bin to have ${targetBinConfiguration?.numberOfCompartments} compartments`
        );
      } else if (selectedBin === 0) {
        mixpanelTrack({
          trackedPageName,
          label: isRetry
            ? "Retry Confirm Configuration"
            : "Confirm Configuration",
          type: "Button Click",
          eventProperties: {
            "Port Id": portId,
            "Port Side Index": portSideIndex,
            Action: "Next Empty Bin"
          }
        });
        await nextEmptyBin({
          autostoreGridId: workstation.autostoreGridId,
          portId
        }).unwrap();
      }
    } catch {
      // error is handled by alert
    }
  };

  if (!!confirmBinReconfigurationError || !!nextEmptyBinError) {
    return (
      <Alert
        variant="outlined"
        severity="error"
        action={
          <ProgressButton onClick={() => void handleConfirm(true)}>
            Retry
          </ProgressButton>
        }
      >
        {getMessageFromRtkError(
          confirmBinReconfigurationError ?? nextEmptyBinError
        )}
      </Alert>
    );
  }

  return (
    <ProgressButton
      sx={{ boxSizing: "border-box", p: 0 }}
      onClick={() => void handleConfirm()}
      buttonSize="large"
      disabled={
        activityState !== "Bin Opened" ||
        confirmBinReconfigurationLoading ||
        nextEmptyBinLoading
      }
      fullWidth
    >
      {t("confirm configuration")}
    </ProgressButton>
  );
};
